<script setup lang="ts">
import type { MarkdownParsedContent } from '@nuxt/content'
import type { LocationQueryRaw, RouteLocationRaw } from '#vue-router'
import { YSC_API_ERROR, YSC_API_SEARCH_ERROR } from '~/utils/error/YscErrorClasses'

export interface CamperTypeContent extends MarkdownParsedContent {
  picture: string
}

const { $errorManager } = useErrorManager()
const { $api } = useYscApi()
const route = useRoute()
const { stats } = useStatsStore()

const slug = computed<string>(() => {
  return paramValueToString(route.params.slug)
})

const { landingDefinition } = useCamperTypeLandingDefinition(slug)

const findOneLocalizedContentWithFallback = useFindOneLocalizedContentWithFallback()

const { data: mainContentFile } = await useAsyncData(`page-camper-type-${slug.value}-content`, async () => {
  if (!landingDefinition.value) {
    return {}
  }

  try {
    const mainContentFile = await findOneLocalizedContentWithFallback<CamperTypeContent>(landingDefinition.value.mainContentFile)

    return {
      mdContent: mainContentFile,
      picture: mainContentFile.picture,
    }
  }
  catch (e) {
    if (e instanceof Error) {
      throw $errorManager({ e })
    }
    throw e
  }
}, {
  watch: [landingDefinition],
})

const { data: secondaryContentFile } = await useAsyncData(`page-camper-type-${slug.value}-secondary-content`, async () => {
  if (!landingDefinition.value) {
    return {}
  }

  try {
    const secondaryContentFile = await findOneLocalizedContentWithFallback<CamperTypeContent>(landingDefinition.value.secondaryContentFile)

    return {
      mdContent: secondaryContentFile,
    }
  }
  catch (e) {
    if (e instanceof Error) {
      throw $errorManager({ e })
    }
    throw e
  }
}, {
  watch: [landingDefinition],
})

const { data: mainSearch } = await useAsyncData(`page-camper-type-${landingDefinition.value}-campers`, async () => {
  if (!landingDefinition.value) {
    return { results: [] }
  }

  try {
    const mainSearch = await $api.search.searchProducts({
      ...landingDefinition.value.searchQuery,
      page_size: 3,
    })

    return mainSearch
  }
  catch (e) {
    if (e instanceof Error) {
      throw $errorManager({ e, name: YSC_API_SEARCH_ERROR })
    }
  }
}, {
  watch: [landingDefinition],
})

const { data: additionalSearches } = useLazyAsyncData(`pages-camper-type-${slug.value}-additional-searches`, async () => {
  if (!landingDefinition.value) {
    return []
  }
  const additionalSearchesPromisesResults = await Promise.allSettled(landingDefinition.value.additionalSearchesDefinition.map(async (def) => {
    try {
      const data = await $api.search.searchProducts({
        ...def.searchQuery,
        page_size: 3,
      })

      const link: RouteLocationRaw = { name: 's', query: def.searchQuery as LocationQueryRaw }
      return {
        data: data,
        title: def.title,
        description: def.description,
        link,
        key: def.key,
      }
    }
    catch (e) {
      if (e instanceof Error) {
        throw $errorManager({ e, name: YSC_API_ERROR })
      }
      throw e
    }
  }))

  return additionalSearchesPromisesResults.filter(isPromiseFulfilledResult).map(p => p.value)
})

const { data: countries } = await useAsyncData(`page-camper-type-${landingDefinition.value}-countries`, async () => {
  try {
    const response = await $api.geolocation.getPlaces({ place_type: 'COUNTRY', allowed_on_home: true })
    return response.results
  }
  catch (e) {
    if (e instanceof Error) {
      throw $errorManager({ e, name: YSC_API_ERROR })
    }
  }
})

const safeCampers = computed(() => mainSearch.value ? mainSearch.value : { results: [] })

const safeAdditionalSearches = computed(() => additionalSearches.value ? additionalSearches.value : [])

const searchLink = computed((): RouteLocationRaw | undefined => landingDefinition.value ? { name: 's', query: landingDefinition.value.searchQuery as LocationQueryRaw } : undefined)

const { fetchLandingSeatsLink } = useFetchLandingSeatsLinks()

const { data: landingSeatsLinks } = useAsyncData(`pages-camper-type-landing-seats-link`, async () => {
  const links = await fetchLandingSeatsLink()

  return links.map(({ name, nxt_url }) => ({ key: `seats-${name}`, name, url: nxt_url }))
})
</script>

<template>
  <div v-if="landingDefinition !== undefined">
    <AppPageHero
      :bg-image="landingDefinition.hero.image"
      :bg-image-alt="landingDefinition.hero.titleSeo"
      :extra-query="landingDefinition.searchQuery"
    >
      <template #title-big>
        {{ landingDefinition.hero.title }}
      </template>
      <template #title-seo>
        {{ landingDefinition.hero.titleSeo }}
      </template>
    </AppPageHero>
    <AppPageHeroBanner
      :average="stats.average"
      :count="stats.count"
    />

    <AppPageSection v-if="landingDefinition.searchQuery">
      <div class="flex flex-col items-center gap-8">
        <AppSectionCampers
          :campers="safeCampers.results"
          class="self-stretch"
        >
          <template #title>
            {{ landingDefinition.sectionCampers.title }}
          </template>
          <template #description>
            {{ landingDefinition.sectionCampers.description }}
          </template>
        </AppSectionCampers>
        <div>
          <NuxtLink
            :to="searchLink"
            class="btn btn-primary"
          >
            {{ landingDefinition.rentLinkLabel }}
          </NuxtLink>
        </div>
      </div>
    </AppPageSection>

    <AppPageSection
      v-if="mainContentFile"
    >
      <div
        v-if="mainContentFile"
        class="md:flex md:items-center md:justify-between"
      >
        <ContentRenderer
          class="prose w-max-none md:w-3/5"
          :value="mainContentFile.mdContent"
        />
        <div class="aspect-video rounded md:w-2/5 overflow-visible">
          <TwicImg
            :src="mainContentFile.picture"
            ratio="none"
            mode="contain"
            class="h-full"
          />
        </div>
      </div>
    </AppPageSection>

    <AppPageSection>
      <AppSectionCampers
        v-for="additionalSearch in safeAdditionalSearches"
        :key="additionalSearch.key"
        :campers="additionalSearch.data.results"
        class="self-stretch"
      >
        <template #title>
          {{ additionalSearch.title }}
        </template>
        <template #description>
          {{ additionalSearch.description }}
        </template>
        <template #action>
          <NuxtLink
            :to="additionalSearch.link"
            class="btn btn-secondary"
          >
            {{ landingDefinition.rentLinkLabel }}
          </NuxtLink>
        </template>
      </AppSectionCampers>
    </AppPageSection>

    <AppPageSection
      v-if="secondaryContentFile"
      class=" w-full max-w-full prose"
    >
      <ContentRenderer
        :value="secondaryContentFile.mdContent"
      />
    </AppPageSection>

    <AppHowItWorksSection user-type="guest">
      <template #title>
        {{ landingDefinition.sectionHowItWorks.title }}
      </template>
      <template #subtitle>
        {{ landingDefinition.sectionHowItWorks.subtitle }}
      </template>
    </AppHowItWorksSection>

    <AppPageSection v-if="landingDefinition.faq && landingDefinition.faq.items.length">
      <div class="mx-auto max-w-screen-md space-y-8">
        <h3 class="text-center text-xl font-semibold lg:text-3xl">
          {{ landingDefinition.faq.title }}
        </h3>
        <YscCollapseGroup :items="landingDefinition.faq.items" />
      </div>
    </AppPageSection>

    <AppLandingOwnerCreateCamper>
      <template #title>
        {{ landingDefinition.sectionOwner.title }}
      </template>
    </AppLandingOwnerCreateCamper>

    <AppLandingCountries
      v-if="countries"
      :countries="countries"
      :additional-links="landingSeatsLinks ?? undefined"
    />
  </div>
</template>
